import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/TextArea';
import { Badge } from '../components/ui/Badge';
import { useToast } from '../components/ui/Toast';
import { usePlaybooksApi, PlaybookVersion } from '../apis/playbooks-apis';

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playbookId: string;
  playbookName: string;
  currentVersion?: number;
  onPublished: (version: PlaybookVersion) => void;
}

const PublishDialog: React.FC<PublishDialogProps> = ({
  open,
  onOpenChange,
  playbookId,
  playbookName,
  currentVersion,
  onPublished,
}) => {
  const [changeNote, setChangeNote] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();

  const nextVersion = (currentVersion ?? 0) + 1;

  const handlePublish = async () => {
    try {
      setIsPublishing(true);
      const version = await playbooksApi.publishVersion(playbookId, changeNote || undefined);
      addToast({
        title: "Success",
        description: `Version ${version.version} of "${playbookName}" has been published`,
        variant: "default",
        duration: 4000,
      });
      onPublished(version);
      setChangeNote('');
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error publishing version:', error);
      addToast({
        title: "Error",
        description: error.message || 'Failed to publish version',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Publish Version</DialogTitle>
          <DialogDescription className="mt-2">
            <span className="flex items-center gap-2">
              Publishing as{' '}
              <Badge className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white">
                Version {nextVersion}
              </Badge>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Change Note (optional)
          </label>
          <Textarea
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value.slice(0, 500))}
            placeholder="Describe what changed in this version..."
            className="min-h-[100px]"
            maxLength={500}
          />
          <p className="text-xs text-gray-500 mt-1 text-right">
            {changeNote.length}/500
          </p>
        </div>

        <DialogFooter className="p-6 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isPublishing}
            className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white"
          >
            {isPublishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publishing...
              </>
            ) : (
              <>Publish Version {nextVersion}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishDialog;
